// Helpers compartilhados pelos builders de formulário (Pass 1 PDF).
//
// Tudo PURO e em pt-BR hardcoded — estes são documentos legais de língua única
// (paridade com o comprovante do intake, que também não usa i18n no conteúdo do
// PDF). i18n fica para os RÓTULOS DE BOTÃO na UI, não para o corpo do documento.

import { comiteCompetencia, type DenunciaState } from '@aions/core';

/** Membro projetado pelo endpoint GET /api/comite/members. */
export interface MemberInfo {
  id: string;
  email: string;
  subRole: string | null;
  /** É o gestor único (`admin_master`)? Usado pelo re-wrap dirigido do CaseDetailPage.
   *  Opcional: worker antigo não manda o campo, e a ausência degrada para "não é". */
  adminMaster?: boolean;
}

/** Entrada comum dos builders. `state` carrega openedAt/triagem/decisao/etc. */
export interface FormInput {
  caseId: string;
  categoria: string;
  createdWeek: string;
  state: DenunciaState;
  attachments: ReadonlyArray<{ contentHash: string; sizeBucket: string }>;
  members: ReadonlyArray<MemberInfo>;
}

const CATEGORIA_LABELS: Readonly<Record<string, string>> = {
  assedio_moral: 'Assédio Moral',
  assedio_sexual: 'Assédio Sexual',
  discriminacao: 'Discriminação',
  discriminacao_salarial_genero: 'Discriminação Salarial de Gênero',
  violencia_no_trabalho: 'Violência no Trabalho',
  corrupcao: 'Corrupção',
  fraude: 'Fraude',
  conflito_interesse: 'Conflito de Interesse',
  seguranca_saude: 'Segurança e Saúde',
  violacao_dados_lgpd: 'Violação de Dados Pessoais (LGPD)',
  violacao_conduta_etica: 'Violação do Código de Ética/Conduta',
  outros: 'Outros',
};

const COMPETENCIA_LABELS: Readonly<Record<string, string>> = {
  compliance: 'Compliance / Jurídico',
  rh: 'RH',
  sesmt: 'SESMT',
};

const GRAVIDADE_LABELS: Readonly<Record<string, string>> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
};

const ROTA_LABELS: Readonly<Record<string, string>> = {
  padrao: 'Padrão',
  com_membro_externo: 'Com membro externo',
};

// ⚠️ `compras` entrou com a migration 016, JÁ APLICADA em prod — sem esta linha,
// o Termo de Sigilo de um membro de Compras imprimia o slug cru `compras`.
// Não era regressão futura: era defeito vivo no documento arquivado.
const SUBROLE_LABELS: Readonly<Record<string, string>> = {
  rh: 'RH',
  juridico: 'Jurídico',
  sesmt: 'SESMT',
  compras: 'Compras / Suprimentos',
  externo: 'Externo',
};

export function categoriaLabel(slug: string): string {
  return CATEGORIA_LABELS[slug] ?? slug;
}

export function competenciaLabel(categoria: string): string {
  const comp = comiteCompetencia(categoria);
  return comp === null ? '—' : (COMPETENCIA_LABELS[comp] ?? '—');
}

export function gravidadeLabel(g: string | undefined): string {
  return g === undefined ? '' : (GRAVIDADE_LABELS[g] ?? g);
}

export function rotaLabel(r: string | undefined): string {
  return r === undefined ? '' : (ROTA_LABELS[r] ?? r);
}

export function subRoleLabel(s: string | null): string {
  return s === null ? '—' : (SUBROLE_LABELS[s] ?? s);
}

/** Data DD/MM/AAAA em UTC (determinístico — sem depender de locale do runtime). */
export function fmtDate(epochMs: number): string {
  const d = new Date(epochMs);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${String(d.getUTCFullYear())}`;
}

/** Rótulo de um membro a partir do diretório: "email (SubRole)" ou o ulid cru. */
export function memberLabel(members: ReadonlyArray<MemberInfo>, ulid: string): string {
  const m = members.find((x) => x.id === ulid);
  if (!m) return ulid; // não encontrado no diretório → mostra o ulid (não inventa)
  return `${m.email} (${subRoleLabel(m.subRole)})`;
}

// ---- Acessos seguros ao DenunciaState (discriminated union) -----------------

export function getOpenedAt(state: DenunciaState): number | null {
  return 'openedAt' in state ? state.openedAt : null;
}

export function getTriagem(
  state: DenunciaState,
): { gravidade: string; rota: string; classifiedBy: string; classifiedAt: number } | null {
  if ('triagem' in state && state.triagem) {
    const t = state.triagem;
    return {
      gravidade: t.gravidade,
      rota: t.rota,
      classifiedBy: t.classifiedBy,
      classifiedAt: t.classifiedAt,
    };
  }
  return null;
}

export function getAssignedMembers(state: DenunciaState): ReadonlyArray<string> {
  return 'assignedMembers' in state ? state.assignedMembers : [];
}

export function getDecisao(state: DenunciaState): {
  kind: string;
  votoTally: { procedente: number; improcedente: number; improcedente_insuficiencia: number };
  emittedAt: number;
  emittedBy: string;
  prazoRecursalAteEpochMs: number;
} | null {
  if ('decisao' in state) {
    const d = state.decisao;
    return {
      kind: d.kind,
      votoTally: d.votoTally,
      emittedAt: d.emittedAt,
      emittedBy: d.emittedBy,
      prazoRecursalAteEpochMs: d.prazoRecursalAteEpochMs,
    };
  }
  return null;
}

const DECISAO_LABELS: Readonly<Record<string, string>> = {
  procedente: 'Procedente',
  improcedente: 'Improcedente',
  improcedente_insuficiencia: 'Improcedente por insuficiência de provas',
};

export function decisaoLabel(kind: string): string {
  return DECISAO_LABELS[kind] ?? kind;
}
